// src/components/Analytics.tsx

/**
 * @fileoverview Client-side route tracking component for Next.js SPA navigation.
 * @description Tracks page views when users navigate between routes in the single-page application.
 * This component works in conjunction with GoogleAnalytics.tsx to provide complete analytics coverage.
 * 
 * Component Architecture:
 * - GoogleAnalytics.tsx: Loads GA4 scripts + Consent Mode v2, and configures GA4
 *   with send_page_view:false (so it does NOT auto-fire any page_view).
 * - Analytics.tsx (this file): Emits the SINGLE authoritative page_view event on
 *   every route change, INCLUDING the initial load (the effect runs on mount).
 *
 * Why this component is needed:
 * GA4 is configured with send_page_view:false to avoid duplicate pageviews, so
 * every page_view — first paint and subsequent client-side navigations — comes
 * from here. (Keep GA4 Enhanced Measurement's "page changes based on browser
 * history events" OFF for this property, or pageviews double-count.)
 *
 * @module components/Analytics
 */

"use client"

import { usePathname } from "next/navigation"
import { useEffect, type ReactNode } from "react"
import { hasRegionConsentCookie } from "~/lib/analytics/consent-banner-visibility"
import { invalidateClientContextCache } from "~/lib/analytics/client-context"
import {
  recordAttributionTouch,
  recordAttributionTouchAsyncBounded,
} from "~/lib/analytics/attribution"
import { pageview } from "~/lib/gtag"

/**
 * Client-side route tracking component for Next.js SPA navigation
 *
 * Emits the single authoritative `page_view` event on every route change,
 * including the initial load (the effect runs on mount) — GA4 is configured
 * with send_page_view:false, so this component owns ALL pageviews.
 *
 * Key features:
 * - Consent + GA-readiness are handled inside pageview() (consent is cached with
 *   event-driven invalidation; see ~/lib/analytics/consent).
 * - Avoids useSearchParams() to prevent Suspense boundary requirements; reads the
 *   query string directly from window.location.
 *
 * @returns null (this component only performs side effects)
 */
const Analytics = (): ReactNode => {
  // Get current pathname from Next.js router
  // This triggers re-renders on route changes, enabling automatic tracking
  const pathname = usePathname()

  useEffect(() => {
    if (typeof window === "undefined" || !pathname) return

    const query = window.location.search || ""
    const url = `${pathname}${query}`

    const run = async (): Promise<void> => {
      // When the region cookie is already stamped, the sync attribution path can
      // write immediately — record first so `storage_attribution_present` in the
      // cached client context reflects the first touch on this pageview.
      if (hasRegionConsentCookie()) {
        recordAttributionTouch(pathname)
        invalidateClientContextCache()
        pageview(url)
        return
      }
      // No region cookie yet — bounded hydration so the pageview carries
      // attribution context when measurement is allowed (RoW default).
      try {
        await recordAttributionTouchAsyncBounded(pathname)
        invalidateClientContextCache()
      } catch {
        // best-effort
      }
      pageview(url)
    }

    void run()
  }, [pathname])

  // This component only performs side effects and doesn't render anything
  return null
}

export default Analytics